import _ from "lodash";
import React, { useContext } from "react";
import classes from "./BenchmarkView.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { Checkbox, useDSContext } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { getSubjectStandardWithCode } from "Utils";

const SelectedValueContext = React.createContext();

// "x-small" is not accepted in DS checkbox anymore
// hence this function is of no use

// const getCheckboxSize = depth => {
//   if (depth > 2) {
//     return "xx-small";
//   } else if (depth == 2) {
//     return "xx-small";
//   } else {
//     return "x-small";
//   }
// };

const getDepthRange = ({ nodeList }) => {
  const depthArray = [];
  const traverse = node => {
    if (node) {
      const children = _.get(node, "children", []);
      depthArray.push(node.depth);
      if (_.size(children)) {
        _.forEach(children, node => {
          traverse(node);
        });
      }
    }
  };
  _.forEach(nodeList, node => {
    traverse(node);
  });
  //temporary fix for depthArray
  //need to remove the nodes if AssociatedParent node doesn't exist
  return _.sortBy(_.uniq(depthArray));
};

const getListStyle = ({ currentDepth, depthArray, showDiscAtAllLevel }) => {
  const styles = ["none", "disc", "circle", "square"];
  const index = _.indexOf(depthArray, currentDepth);
  if (_.size(depthArray) === 1 && currentDepth > 0) return styles[1];
  return showDiscAtAllLevel ? styles[1] : styles[index % styles.length];
};

const calculateMarginLeft = ({ currentDepth, depthArray }) => {
  const index = _.indexOf(depthArray, currentDepth);
  switch (index) {
    case 0:
      return 0;
    case 1:
      return 2;
    default:
      return (index - 1) * 16;
  }
};

const getChildrenContainerStyle = ({
  startDepth,
  depth,
  calculatedDepth,
  childrenContainerStyle = [],
  defaultTopMargin,
}) => {
  const defaultChildrenContainerStyle = !_.isUndefined(calculatedDepth)
    ? {
        //paddingLeft: Math.max(calculatedDepth - startDepth, 0) * 16
      }
    : { marginTop: `${defaultTopMargin}px` };

  const customStyle = _.get(
    _.find(childrenContainerStyle, { depth }),
    "style",
    {}
  );

  return _.isEmpty(customStyle)
    ? defaultChildrenContainerStyle
    : {
        ...defaultChildrenContainerStyle,
        ...customStyle,
      };
};

const getNodeLabelClass = ({ depth, isDSV2 }) => {
  switch (depth) {
    case 0:
      return classNames("text-label-2", classes.nodeLabel);
    case 1:
    case 2:
      return classNames({
        ["text-body-1"]: !isDSV2,
        ["text-body"]: isDSV2,
        [classes.nodeLabel]: true,
      });

    case 3:
      return classNames("text-body-2", classes.nodeLabel);

    default:
      return classNames("text-body-2", classes.h4Label);
  }
};

const getNodeLabelClassV2 = ({
  depth,
  isParent,
  resolvedSubject,
  isFirstElement,
}) => {
  const depthPlusOne =
    depth === 1 && (!isParent || resolvedSubject) ? depth + 1 : depth;
  switch (depthPlusOne) {
    case 0:
      return classNames("text-body-1", classes.nodeLabelV2);
    case 1:
      return classNames("text-body-1", classes.nodeLabelV2);
    case 2:
      return classNames(
        "text-body-2",
        isParent
          ? classes.nodeLabel
          : isFirstElement
          ? classes.firstNodeLabelV2
          : classes.nodeLabelV2Child
      );
    case 3:
      return classNames("text-body-1", classes.nodeLabelV2);
    default:
      return classNames("text-body-2", classes.h4Label);
  }
};

const getNodeLabelStyle = ({
  depth,
  showCheckbox,
  nodeLabelStyle = [],
  isReverseCheckbox,
  dynamicLevelContainerStyle,
}) => {
  const defaultNodeLabelStyle = showCheckbox ? { cursor: "pointer" } : {};

  if (isReverseCheckbox && showCheckbox) {
    defaultNodeLabelStyle.flexDirection = "row-reverse";
  }

  let customStyle = _.get(_.find(nodeLabelStyle, { depth }), "style", {});
  if (!_.isEmpty(dynamicLevelContainerStyle)) {
    customStyle = {
      ...customStyle,
      ..._.get(dynamicLevelContainerStyle, depth, {}),
    };
  }
  return _.isEmpty(customStyle)
    ? defaultNodeLabelStyle
    : {
        ...defaultNodeLabelStyle,
        ...customStyle,
      };
};

const getUniqueNodeIdsByStandardSetId = nodes => {
  return _.chain(nodes)
    .uniqBy(node => _.get(node, "standardsWrapper.standardSet.id"))
    .map("id")
    .value();
};

const NodeTree = React.memo(props => {
  const {
    node,
    isTagging,
    loopDepth,
    startDepth,
    depthOffset,
    nodeLabelStyle,
    nodeSpanStyle,
    dynamicLevelContainerStyle,
    childrenContainerStyle,
    isReverseCheckbox,
    checkboxSize,
    searchTerm,
    checkboxItemStyle,
    checkboxContainerStyle,
    sortedNodeIds,
    labelStyle,
    isDsComponent,
    changeTextView,
    testIdPrefix,
    showDiscAtSecondLevel,
    defaultTopMargin,
    depthArray,
    shouldCheckIsLeafProperty = true,
    showStandardsLabel = false,
    nodeIndex,
    skipNodeLevelCheck,
    parentStandardIds = [],
    showDiscAtAllLevel = false,
    allowParentTagging = false,
    showSetTitle = false,
  } = props;

  const {
    id,
    label,
    subText,
    children,
    depth,
    code,
    genericTags,
    tags,
    resolvedSubject,
    isArchived,
    parent,
    type,
    standardsWrapper = {},
  } = node;

  let isFirstElement = false;
  if (_.isNil(parent)) isFirstElement = true;

  const updatedDepth = depth + depthOffset;
  const nodeTags = !_.isEmpty(genericTags) ? genericTags : tags;

  // for some views, might need to offset the depth to start one or two level lower
  const calculatedDepth =
    (_.has(node, "depth") ? depth : loopDepth) + depthOffset;

  const updatedChildrenContainerStyle = getChildrenContainerStyle({
    startDepth,
    depth: updatedDepth + 1,
    calculatedDepth,
    childrenContainerStyle,
    defaultTopMargin,
  });

  const isLeaf =
    shouldCheckIsLeafProperty && _.has(node, "isLeaf") && !_.isNull(node.isLeaf)
      ? node.isLeaf
      : _.get(children, "length", 0) == 0;

  if (isLeaf || allowParentTagging) {
    // this is for multi select functionality
    sortedNodeIds.push(id);
  }

  let _isArchived = isArchived;
  if (!_.isNil(resolvedSubject) && _.isNil(_isArchived))
    _isArchived = _.get(resolvedSubject, "isArchived", false);
  return (
    <div className={classes.nodeBody}>
      {standardsWrapper?.standardSet?.title &&
      (skipNodeLevelCheck || depth == 1) &&
      (_.includes(parentStandardIds, id) || nodeIndex === 0) &&
      showStandardsLabel &&
      (!parent || showSetTitle) ? (
        <div
          className={classNames({
            [classes.standardTitle]: true,
            ["text-label-3"]: true,
          })}
        >
          {standardsWrapper?.standardSet?.title}
        </div>
      ) : null}

      <NodeItem
        id={id}
        isFirstElement={isFirstElement}
        nodeIndex={nodeIndex}
        isArchived={_isArchived}
        label={label}
        subText={subText}
        isLeaf={isLeaf}
        isTagging={isTagging}
        calculatedDepth={calculatedDepth}
        nodeLabelStyle={nodeLabelStyle}
        nodeSpanStyle={nodeSpanStyle}
        dynamicLevelContainerStyle={dynamicLevelContainerStyle}
        code={code}
        isReverseCheckbox={isReverseCheckbox}
        checkboxSize={checkboxSize}
        searchTerm={searchTerm}
        checkboxItemStyle={checkboxItemStyle}
        genericTags={nodeTags}
        checkboxContainerStyle={checkboxContainerStyle}
        labelStyle={labelStyle}
        isDsComponent={isDsComponent}
        changeTextView={changeTextView}
        showDiscAtSecondLevel={showDiscAtSecondLevel}
        resolvedSubject={resolvedSubject}
        testIdPrefix={testIdPrefix}
        defaultTopMargin={defaultTopMargin}
        depthArray={depthArray}
        depth={depth}
        type={type}
        standardsWrapper={standardsWrapper}
        showStandardsLabel={showStandardsLabel}
        showDiscAtAllLevel={showDiscAtAllLevel}
        allowParentTagging={allowParentTagging}
      />
      {!isLeaf && (
        <div
          className={classes.treeContainer}
          style={updatedChildrenContainerStyle}
        >
          {_.map(children, (child, index) => {
            return (
              <NodeTree
                node={child}
                key={child.id}
                isTagging={isTagging}
                startDepth={startDepth}
                loopDepth={loopDepth + 1}
                depthOffset={depthOffset}
                nodeLabelStyle={nodeLabelStyle}
                nodeSpanStyle={nodeSpanStyle}
                dynamicLevelContainerStyle={dynamicLevelContainerStyle}
                childrenContainerStyle={childrenContainerStyle}
                isReverseCheckbox={isReverseCheckbox}
                checkboxSize={checkboxSize}
                searchTerm={searchTerm}
                checkboxItemStyle={checkboxItemStyle}
                checkboxContainerStyle={checkboxContainerStyle}
                sortedNodeIds={sortedNodeIds}
                changeTextView={changeTextView}
                testIdPrefix={testIdPrefix}
                showDiscAtSecondLevel={showDiscAtSecondLevel}
                defaultTopMargin={defaultTopMargin}
                depthArray={depthArray}
                shouldCheckIsLeafProperty={shouldCheckIsLeafProperty}
                showStandardsLabel={showStandardsLabel}
                nodeIndex={index}
                showDiscAtAllLevel={showDiscAtAllLevel}
                allowParentTagging={allowParentTagging}
                showSetTitle={showSetTitle}
              ></NodeTree>
            );
          })}
        </div>
      )}
    </div>
  );
});

const NodeItem = React.memo(props => {
  const { t } = useI18n(["common"]);
  const {
    label,
    depth,
    calculatedDepth,
    isLeaf,
    isTagging,
    id,
    nodeLabelStyle = [],
    code,
    isReverseCheckbox,
    checkboxItemStyle,
    nodeSpanStyle,
    genericTags,
    dynamicLevelContainerStyle,
    checkboxContainerStyle,
    labelStyle,
    isDsComponent,
    isArchived,
    isFirstElement,
    changeTextView,
    resolvedSubject,
    testIdPrefix,
    showDiscAtSecondLevel,
    defaultTopMargin,
    depthArray,
    subText,
    type,
    standardsWrapper = {},
    showStandardsLabel = false,
    showDiscAtAllLevel,
    nodeIndex,
    allowParentTagging = false,
  } = props;
  const { dsVersion } = useDSContext();

  const isDSV2 = dsVersion === "2.0";

  const { selectedValues, toggleCheckbox } = useContext(SelectedValueContext);
  const depthMinusOne = calculatedDepth - 1;
  const nodeLabelClass = changeTextView
    ? getNodeLabelClassV2({
        depth: depthMinusOne >= 0 ? depthMinusOne : 0,
        isParent: !(isLeaf && isTagging),
        resolvedSubject,
        isFirstElement:
          !nodeIndex && _.size(depthArray) === 1 && changeTextView,
      })
    : getNodeLabelClass({
        depth: depthMinusOne >= 0 ? depthMinusOne : 0,
        isDSV2,
      });

  const isSelected = _.includes(selectedValues, id);
  const showCheckbox = (isLeaf || allowParentTagging) && isTagging;
  const updatedNodeLabelStyle = getNodeLabelStyle({
    depth: calculatedDepth,
    showCheckbox,
    nodeLabelStyle,
    isReverseCheckbox,
    dynamicLevelContainerStyle,
  });

  const bullet = classNames(
    { [classes.bullet]: !isReverseCheckbox },
    { [classes.bulletNoMargin]: isReverseCheckbox }
  );

  const styleClassMapping = {
    none: classes.none,
    circle: classes.circle,
    disc: classes.disc,
    square: classes.square,
  };

  const listStyle = getListStyle({
    currentDepth: depth,
    depthArray,
    showDiscAtAllLevel,
  });

  const listStyles = classNames({
    [styleClassMapping[listStyle]]: !showCheckbox,
  });

  const standardLabelWithCode = getSubjectStandardWithCode({
    label,
    code,
    subText,
    containerClass: showDiscAtAllLevel && depth === 1 ? {} : listStyles,
    type,
  });

  const leftMargin = calculateMarginLeft({
    currentDepth: depth,
    depthArray,
  });

  const depthIndex = _.indexOf(depthArray, depth);
  const parentTaggingMarginLeft =
    allowParentTagging && depthIndex > 0 ? depthIndex * 28 : 0;

  const spanStyles = classNames({
    [classes.spanStyle]: true,
    [classes.spanStyleV2]: isDSV2,
    [classes.hideDepthZero]:
      showStandardsLabel && depth === 0 && _.isEmpty(standardsWrapper),
  });

  return (
    <div
      className={nodeLabelClass}
      style={{
        ...updatedNodeLabelStyle,
        ...(parentTaggingMarginLeft > 0 && {
          paddingLeft: `${parentTaggingMarginLeft}px`,
        }),
      }}
      onClick={event => {
        if (showCheckbox) {
          const shouldSelectRange = event.shiftKey;
          toggleCheckbox({ id, isSelected, shouldSelectRange });
        }
      }}
      data-test-id={`${testIdPrefix}-node-${id}`}
      data-checked={isSelected}
    >
      {showCheckbox ? (
        <div className={bullet} style={checkboxContainerStyle}>
          <Checkbox
            // style={{ width: checkboxUpdatedSize, height: checkboxUpdatedSize }}
            // fill={colors.blue400}
            // offFill={colors.strokeTwo}
            isChecked={isSelected}
            // size={getCheckboxSize(depth)}
            size={"xx-small"}
            // checkboxItemStyle={checkboxItemStyle}
            testId={testIdPrefix}
          />
        </div>
      ) : null}
      <span
        style={isDsComponent ? labelStyle : nodeSpanStyle}
        className={spanStyles}
      >
        <div
          className={classes.labelContainer}
          style={{ marginLeft: `${leftMargin}px` }}
        >
          {standardLabelWithCode}

          {isFirstElement && isArchived ? (
            <span className={classes.archived}>({t("common:archived")})</span>
          ) : null}
        </div>
      </span>
      {/* {`${label}`} */}
    </div>
  );
});

class BenchmarkView extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      lastSelectedNode: null,
    };
  }

  // an array of sequenced nodes
  sortedNodeIds = [];

  // get index of node from its id
  getIdxFromNodeId = id => {
    return _.indexOf(this.sortedNodeIds, id);
  };

  // get the start and end index of range we want to select
  getSelectionRange = id => {
    const currentIdx = this.getIdxFromNodeId(id),
      lastIdx = this.getIdxFromNodeId(this.state.lastSelectedNode);

    // should be in increasing order so as to iterate uniformly
    const selectionStartIdx = currentIdx > lastIdx ? lastIdx : currentIdx,
      selectionEndIdx = currentIdx > lastIdx ? currentIdx : lastIdx;

    return { selectionStartIdx, selectionEndIdx };
  };

  // check if all of nodes in range are selected
  isRangeSelected = ({ selectionStartIdx, selectionEndIdx, excludeId }) => {
    const { selectedValues } = this.props;
    let selected = true;

    for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
      const nodeId = this.sortedNodeIds[i];

      // exclude the beginning index that we just deselected
      // so that it doesn't trigger the break condition after this one
      if (_.isEqual(excludeId, nodeId)) continue;

      if (!_.includes(selectedValues, nodeId)) {
        selected = false;
        break;
      }
    }

    return selected;
  };

  toggleCheckbox = ({ id, isSelected, shouldSelectRange }) => {
    let { selectedValues } = this.props;
    const { lastSelectedNode } = this.state;

    // if Shift is pressed - range selection
    if (shouldSelectRange && lastSelectedNode) {
      const { selectionStartIdx, selectionEndIdx } = this.getSelectionRange(id);

      // [only while DESELECTING range]
      // by deselecting the beginning index, we deselected a part of the range
      // so we exclude it from checks so isRangeSelected doesn't return false
      const excludeId = _.includes(selectedValues, id)
        ? lastSelectedNode
        : null;

      // if range is already selected - deselect
      if (
        this.isRangeSelected({ selectionStartIdx, selectionEndIdx, excludeId })
      ) {
        const nodesToRemove = [];
        for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
          const nodeId = this.sortedNodeIds[i];
          nodesToRemove.push(nodeId);
        }

        selectedValues = _.filter(
          selectedValues,
          nodeId => !_.includes(nodesToRemove, nodeId)
        );
      } else {
        // else select the range
        for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
          const nodeId = this.sortedNodeIds[i];

          // prevent duplicates
          if (_.indexOf(selectedValues, nodeId) === -1)
            selectedValues = [...selectedValues, nodeId];
        }
      }

      // reset the beginning index
      this.setState({ lastSelectedNode: null });
    } else {
      // single selection
      if (isSelected) {
        selectedValues = _.filter(selectedValues, val => val != id);
      } else {
        selectedValues = [...selectedValues, id];
      }

      // this will be used as the beginning index for selection range
      this.setState({ lastSelectedNode: id });
    }
    this.updateValue(selectedValues);
  };

  renderView = () => {
    const {
      value,
      isTagging,
      startDepth,
      depthOffset,
      selectedValues,
      nodeLabelStyle,
      nodeSpanStyle,
      dynamicLevelContainerStyle,
      childrenContainerStyle,
      isReverseCheckbox,
      checkboxSize,
      searchTerm,
      checkboxItemStyle,
      checkboxContainerStyle,
      labelStyle,
      isDsComponent,
      changeTextView,
      testIdPrefix,
      showDiscAtSecondLevel,
      defaultTopMargin,
      shouldCheckIsLeafProperty = true,
      showStandardsLabel = false,
      skipNodeLevelCheck = false,
      showDiscAtAllLevel = false,
      allowParentTagging = false,
      showSetTitle = false,
    } = this.props;
    const depthArray = getDepthRange({
      nodeList: value,
    });
    const updatedChildrenContainerStyle = getChildrenContainerStyle({
      depth: startDepth,
      startDepth,
      childrenContainerStyle,
      defaultTopMargin,
    });
    return (
      <SelectedValueContext.Provider
        value={{ selectedValues, toggleCheckbox: this.toggleCheckbox }}
      >
        <div
          style={updatedChildrenContainerStyle}
          className={classes.treeContainer}
        >
          {_.map(value, (node, index) => {
            if (node) {
              return (
                <NodeTree
                  node={node}
                  key={node.id}
                  isTagging={isTagging}
                  loopDepth={startDepth}
                  startDepth={startDepth}
                  depthOffset={depthOffset}
                  nodeLabelStyle={nodeLabelStyle}
                  nodeSpanStyle={nodeSpanStyle}
                  dynamicLevelContainerStyle={dynamicLevelContainerStyle}
                  childrenContainerStyle={childrenContainerStyle}
                  isReverseCheckbox={isReverseCheckbox}
                  checkboxSize={checkboxSize}
                  searchTerm={searchTerm}
                  checkboxItemStyle={checkboxItemStyle}
                  checkboxContainerStyle={checkboxContainerStyle}
                  sortedNodeIds={this.sortedNodeIds}
                  labelStyle={labelStyle}
                  isDsComponent={isDsComponent}
                  changeTextView={changeTextView}
                  testIdPrefix={testIdPrefix}
                  showDiscAtSecondLevel={showDiscAtSecondLevel}
                  defaultTopMargin={defaultTopMargin}
                  depthArray={depthArray}
                  shouldCheckIsLeafProperty={shouldCheckIsLeafProperty}
                  showStandardsLabel={showStandardsLabel}
                  skipNodeLevelCheck={skipNodeLevelCheck}
                  parentStandardIds={
                    showStandardsLabel
                      ? getUniqueNodeIdsByStandardSetId(value)
                      : null
                  }
                  nodeIndex={index}
                  showDiscAtAllLevel={showDiscAtAllLevel}
                  allowParentTagging={allowParentTagging}
                  showSetTitle={showSetTitle}
                ></NodeTree>
              );
            }
          })}
        </div>
      </SelectedValueContext.Provider>
    );
  };
}

NodeTree.displayName = "NodeTree";
NodeItem.displayName = "NodeItem";

BenchmarkView.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isTagging: false,
  startDepth: 0,
  selectedValues: [],
  depthOffset: 0,
  isReverseCheckbox: false,
  searchTerm: "",
  checkboxItemStyle: {},
  nodeSpanStyle: {},
  dynamicLevelContainerStyle: {},
  checkboxContainerStyle: {},
  labelStyle: {},
  isDsComponent: false,
  changeTextView: false,
  testIdPrefix: "benchmark-view",
  showDiscAtSecondLevel: false,
  defaultTopMargin: 0,
};

BenchmarkView.propTypes = {
  ...UIBaseComponent.propTypes,
};

export default BenchmarkView;

/*
type nodeLabelStyle {
  depth: number
  style: React.CSSProperties
}
nodeLabelStyle[]
*/
